import React, { useState, useContext, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import LayoutMasterDetail from "layouts/LayoutMasterDetail";

import { StoreContext } from "shared/context/store-context";
import { PERMISSION_TYPES } from "@routebot/constants";
import { AuthContext } from "shared/context/auth-context";

import InventoryMaintenanceItem from "./InventoryMaintenanceItem";

import { formatYearMonthDateWithTimezone } from "shared/utility/date";

const InventoryMaintenances = () => {
  const { t } = useTranslation();
  const auth = useContext(AuthContext);
  const itemType = "maintenance";
  const listName = "inventory_maintenances";

  const { maintenances, vehicles, DeleteMaintenance, UpdateMaintenance, InsertMaintenance } = useContext(StoreContext);

  const [pageItems, setPageItems] = useState();

  useEffect(() => {
    setPageItems(maintenances);
  }, [maintenances]);

  const getMaintenanceRowClassName = useCallback((params) => {
    if (params.row.maintenanceStatus === "devam_ediyor") {
      return "maintenance-row-in-progress";
    }
    return params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row";
  }, []);

  // Sonraki bakım tarihi bugünden önceyse (geçmişse) bakım zamanı gelmiş demektir.
  const isNextMaintenanceOverdue = useCallback((date) => {
    if (!date) {
      return false;
    }
    const todayStr = formatYearMonthDateWithTimezone(new Date());
    const nextStr = formatYearMonthDateWithTimezone(date);
    return !!nextStr && nextStr < todayStr;
  }, []);

  const listColumns = [
    {
      field: "vehicle",
      headerName: t("inventoryMaintenance.columns.vehicle"),
      width: 180,
      valueGetter: (params) => vehicles?.find((v) => v.id === params.value)?.plate || "-",
      renderCell: (params) => params.value ?? "-",
    },
    {
      field: "maintenanceStatus",
      headerName: t("inventoryMaintenance.columns.maintenanceStatus"),
      width: 130,
      renderCell: (params) =>
        params.row.maintenanceStatus === "devam_ediyor"
          ? t("inventoryMaintenance.maintenanceStatus.inProgress")
          : t("inventoryMaintenance.maintenanceStatus.finished"),
    },
    {
      field: "maintenanceType",
      headerName: t("inventoryMaintenance.columns.maintenanceType"),
      width: 120,
      renderCell: (params) => {
        if (!params.value) return "-";
        return params.value === "bakım"
          ? t("inventoryMaintenance.maintenanceTypes.maintenance")
          : t("inventoryMaintenance.maintenanceTypes.repair");
      },
    },
    {
      field: "maintenanceDate",
      headerName: t("inventoryMaintenance.columns.maintenanceDate"),
      width: 150,
      renderCell: (params) => (params.value ? formatYearMonthDateWithTimezone(params.value) : "-"),
    },
    {
      field: "maintenanceOdometer",
      headerName: t("inventoryMaintenance.columns.maintenanceOdometer"),
      width: 180,
      renderCell: (params) => (params.value || params.value === 0 ? `${params.value}` : "-"),
    },
    {
      field: "nextMaintenanceDate",
      headerName: t("inventoryMaintenance.columns.nextMaintenanceDate"),
      width: 160,
      cellClassName: (params) => (isNextMaintenanceOverdue(params.value) ? "highlight-red" : undefined),
      renderCell: (params) => {
        if (!params.value) {
          return "-";
        }
        const formatted = formatYearMonthDateWithTimezone(params.value);
        if (isNextMaintenanceOverdue(params.value)) {
          return (
            <span
              style={{ display: "flex", alignItems: "center", gap: 6 }}
              title={t("inventoryMaintenance.nextMaintenanceOverdue")}
            >
              <span>⚠</span>
              <span>{formatted}</span>
            </span>
          );
        }
        return formatted;
      },
    },
    {
      field: "nextMaintenanceOdometer",
      headerName: t("inventoryMaintenance.columns.nextMaintenanceOdometer"),
      width: 180,
      renderCell: (params) => (params.value || params.value === 0 ? `${params.value}` : "-"),
    },
    {
      field: "maintenanceDescription",
      headerName: t("inventoryMaintenance.columns.maintenanceDescription"),
      width: 250,
    },
    {
      field: "serviceCenter",
      headerName: t("inventoryMaintenance.columns.serviceCenter"),
      width: 200,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "serviceCenterPhone",
      headerName: t("inventoryMaintenance.columns.serviceCenterPhone"),
      width: 180,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "cost",
      headerName: t("inventoryMaintenance.columns.cost"),
      width: 120,
      renderCell: (params) => (params.value || params.value === 0 ? `${params.value} ₺` : "-"),
    },
    {
      field: "fatura",
      headerName: t("inventoryMaintenance.columns.fatura"),
      width: 150,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "fis",
      headerName: t("inventoryMaintenance.columns.fis"),
      width: 150,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "usedStocks",
      headerName: t("inventoryMaintenance.usedStocks.columnLabel"),
      width: 150,
      renderCell: (params) => {
        const usedStocks = params.value;
        if (!usedStocks || usedStocks.length === 0) return "-";
        return t("inventoryMaintenance.usedStocks.stockCount", { count: usedStocks.length });
      },
    },
  ];

  const standardHelpItems = [
    t("inventoryMaintenance.help.line1"),
    t("inventoryMaintenance.help.line2"),
    t("inventoryMaintenance.help.line3"),
  ];

  return (
    <LayoutMasterDetail
      itemType={itemType}
      items={pageItems}
      listName={listName}
      listColumns={listColumns}
      hideCreateNewItem={!auth.permissions[PERMISSION_TYPES.ACCESS_TO_WRITE_INVENTORY]}
      SelectedItemComponent={InventoryMaintenanceItem}
      NewItemComponent={InventoryMaintenanceItem}
      DeleteItem={DeleteMaintenance}
      InsertItem={InsertMaintenance}
      UpdateItem={UpdateMaintenance}
      standardHelpItems={standardHelpItems}
      hideCreateNewItemWhenSelected={true}
      getRowClassName={getMaintenanceRowClassName}
    />
  );
};

export default InventoryMaintenances;
